import {
  RELATIONSHIP_TYPES,
  type FilterContext,
} from '@packages/systems/ix3/core';
import {IX3_WF_EXTENSION_KEYS} from '@packages/systems/ix3-wf-plugins/shared';
import {actionPresetTransforms, RawActionPreset} from './actionPreset';
import {RawTimeline, timelineTransforms} from './timeline';
import {interactionTransforms, RawInteraction} from './interaction';
import {IX3SyncParams} from './request';

const DEFAULT_FILTER_CONTEXT: FilterContext = {
  relationship: RELATIONSHIP_TYPES.NONE,
  filterBy: [IX3_WF_EXTENSION_KEYS.CLASS, []],
  firstMatchOnly: false,
};

export function createRawInteraction(params: {
  id: string;
  name: string;
  pageId: string;
  siteId: string;
  overrides?: Partial<RawInteraction>;
}): RawInteraction {
  const {id, name, pageId, siteId, overrides = {}} = params;

  return {
    id,
    name,
    pageId,
    siteId,
    scope: {type: 'pages', value: [pageId]},
    version: 1,
    createdOn: new Date(),
    updatedOn: new Date(),
    triggers: [
      [
        'wf:scroll',
        {
          scrollTriggerConfig: {
            start: '20% bottom',
            end: 'bottom top',
            scrub: null,
            enter: 'play',
            leave: 'none',
            enterBack: 'none',
            leaveBack: 'none',
          },
        },
        [
          IX3_WF_EXTENSION_KEYS.CLASS,
          ['a57eb21f-2ad0-0eb3-a19a-7330f8b74e72'],
          DEFAULT_FILTER_CONTEXT,
        ],
      ],
    ],
    timelineIds: ['t-b2cea1cd'],
    deleted: false,
    ...overrides,
  };
}

export function createRawTimeline(params: {
  id: string;
  name: string;
  pageId: string;
  siteId: string;
  overrides?: Partial<RawTimeline>;
}): RawTimeline {
  const {id, name, pageId, siteId, overrides = {}} = params;

  return {
    id,
    name,
    pageId,
    siteId,
    actions: [],
    version: 1,
    createdOn: new Date(),
    updatedOn: new Date(),
    deleted: false,
    ...overrides,
  };
}

export function createRawActionPreset(params: {
  id: string;
  name: string;
  siteId: string;
  description: string;
  overrides?: Partial<RawActionPreset>;
}): RawActionPreset {
  const {id, name, siteId, description, overrides = {}} = params;

  return {
    id,
    name,
    siteId,
    description,
    type: 'custom',
    version: 1,
    createdOn: new Date(),
    updatedOn: new Date(),
    action: {
      name: 'UnMask',
      color: 'pink',
      timing: {
        duration: 0.5,
        position: 0,
        stagger: {
          amount: 0.5,
        },
        ease: 1,
      },
      tt: 2,
      properties: {
        'wf:transform': {
          y: ['100%', '0%'],
        },
      },
      splitText: {
        type: 'chars',
        mask: 'chars',
      },
    },
    deleted: false,
    ...overrides,
  };
}

export const mockIX3SyncBody: IX3SyncParams = {
  interactions: [
    {
      id: 'test-interaction-id-1',
      pageId: 'test-page-id',
      deleted: false,
      name: 'Interaction 1',
      scope: {type: 'site'},
      triggers: [['wf:test', {}, ['wf:test', '', DEFAULT_FILTER_CONTEXT]]],
      timelineIds: ['test-timeline-id-1'],
      version: 1,
    },
    {
      id: 'test-interaction-id-2',
      pageId: 'test-page-id',
      deleted: false,
      name: 'Interaction 2',
      scope: {type: 'site'},
      triggers: [['wf:test', {}, ['wf:test', '', DEFAULT_FILTER_CONTEXT]]],
      timelineIds: ['test-timeline-id-2'],
      version: 1,
    },
  ],
  timelines: [
    {
      id: 'test-timeline-id-1',
      pageId: 'test-page-id',
      deleted: false,
      actions: [
        {
          id: 'ta-1',
          name: 'Pop in',
          color: 'pink',
          targets: [['wf:trigger-only', '', DEFAULT_FILTER_CONTEXT]],
          timing: {
            duration: 0.25,
            position: 0,
            ease: 1,
          },
          tt: 0,
          properties: {
            'wf:transform': {
              opacity: null,
              x: null,
              y: null,
              scale: [0, null],
            },
          },
        },
      ],
      previewSpeed: 0,
      version: 1,
    },
    {
      id: 'test-timeline-id-2',
      pageId: 'test-page-id',
      deleted: false,
      actions: [
        {
          id: 'ta-2',
          name: 'Pop out',
          color: 'pink',
          targets: [['wf:trigger-only', '', DEFAULT_FILTER_CONTEXT]],
          timing: {
            duration: 0.25,
            position: 0,
            ease: 1,
          },
          tt: 0,
          properties: {
            'wf:transform': {
              opacity: null,
              x: null,
              y: null,
              scale: [0, null],
            },
          },
        },
      ],
      previewSpeed: 0,
      version: 1,
    },
  ],
  actionPresets: [
    {
      id: 'test-action-preset-id-1',
      description: '',
      type: 'swap-class',
      deleted: false,
      name: 'pop in',
      action: {
        name: 'Pop In',
        color: 'pink',
        timing: {
          duration: 0.25,
          position: 0,
          ease: 1,
        },
        tt: 0,
        properties: {
          'wf:transform': {
            opacity: null,
            x: null,
            y: null,
            scale: [0, null],
          },
        },
      },
      version: 1,
    },
    {
      id: 'test-action-preset-id-2',
      type: 'swap-class',
      description: '',
      deleted: false,
      name: 'pop out',
      action: {
        name: 'Pop out',
        color: 'pink',
        timing: {
          duration: 0.25,
          position: 0,
          ease: 1,
        },
        tt: 0,
        properties: {
          'wf:transform': {
            opacity: null,
            x: null,
            y: null,
            scale: [0, null],
          },
        },
      },
      version: 1,
    },
  ],
};
export const mockIX3SyncResponse = {
  ix3Interactions: [
    {
      id: 'test-interaction-id-1',
      version: 2,
    },
    {
      id: 'test-interaction-id-2',
      version: 2,
    },
  ],
  ix3Timelines: [
    {
      id: 'test-timeline-id-1',
      version: 2,
    },
    {
      id: 'test-timeline-id-2',
      version: 2,
    },
  ],
  ix3ActionPresets: [
    {
      id: 'test-action-preset-id-1',
      version: 2,
    },
    {
      id: 'test-action-preset-id-2',
      version: 2,
    },
  ],
};

export const mockDirtyIX3Export = {
  interactions: [
    interactionTransforms.rawInteractionToClient(
      createRawInteraction({
        id: 'test-interaction-id-1',
        name: 'Interaction 1',
        pageId: 'some-page-id',
        siteId: 'some-site-id',
        overrides: {
          version: 1,
        },
      })
    ),
    interactionTransforms.rawInteractionToClient(
      createRawInteraction({
        id: 'test-interaction-id-2',
        name: 'Interaction 2',
        pageId: 'some-page-id',
        siteId: 'some-site-id',
        overrides: {
          version: 1,
        },
      })
    ),
  ],
  timelines: [
    timelineTransforms.rawTimelineToClient(
      createRawTimeline({
        id: 'test-timeline-id-1',
        name: 'Timeline 1',
        pageId: 'some-page-id',
        siteId: 'some-site-id',
        overrides: {
          version: 1,
        },
      })
    ),
    timelineTransforms.rawTimelineToClient(
      createRawTimeline({
        id: 'test-timeline-id-2',
        name: 'Timeline 2',
        pageId: 'some-page-id',
        siteId: 'some-site-id',
        overrides: {
          version: 1,
        },
      })
    ),
  ],
  actionPresets: [
    actionPresetTransforms.rawActionPresetToClient(
      createRawActionPreset({
        id: 'test-action-preset-id-1',
        name: 'Action Preset 1',
        description: 'Description 1',
        siteId: 'some-site-id',
        overrides: {
          version: 1,
        },
      })
    ),
    actionPresetTransforms.rawActionPresetToClient(
      createRawActionPreset({
        id: 'test-action-preset-id-2',
        name: 'Action Preset 2',
        description: 'Description 2',
        siteId: 'some-site-id',
        overrides: {
          version: 1,
        },
      })
    ),
  ],
};
